import { RefObject, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RFQ } from '../../types';
import { trpClient } from '../../client/trpClient';
import { discoverySubStep } from '../../utils/workflowUtils';
import { WizardPos, wizardStepPath } from '../../utils/wizardRoutes';
import type { UploadDocumentsHandle } from '../../components/forms/UploadDocuments';
import { STEP_IDS, SUB_STEP_IDS } from './journeyModel';

interface WizardNavigationInput {
  rfq: RFQ | null;
  /** The id in the URL; used until the RFQ itself has loaded. */
  rfqId: string;
  pos: WizardPos;
  readOnly: boolean;
  /** The furthest step the rail lets the buyer click to. */
  journeyFrontier: number;
  uploadDocsRef: RefObject<UploadDocumentsHandle | null>;
  mainRef: RefObject<HTMLElement | null>;
}

/**
 * Everything that moves the buyer around the wizard: Continue, Back, rail
 * clicks, and the actions Robbie's chips trigger. The page keeps the layout;
 * the rules for where each control lands live here.
 */
export const useWizardNavigation = ({
  rfq, rfqId, pos, readOnly, journeyFrontier, uploadDocsRef, mainRef,
}: WizardNavigationInput) => {
  const navigate = useNavigate();

  const goTo = useCallback(
    (next: WizardPos) => navigate(wizardStepPath(rfq?.rfqId || rfqId, next)),
    [navigate, rfq?.rfqId, rfqId],
  );

  const handleBackToDashboard = useCallback(() => navigate('/dashboard'), [navigate]);

  const handleContinue = async () => {
    // A viewer's Continue is navigation and nothing else — it must never fire
    // the writes the owner's Continue carries (extraction, step completion).
    if (readOnly) {
      if (pos.step < 5) goTo({ step: pos.step + 1, subStep: 1 });
      return;
    }
    if (pos.step === 1) {
      // The upload step shows its own wait and reports it to Robbie.
      const ok = await uploadDocsRef.current?.submit();
      if (!ok) return;
      goTo({ step: 2, subStep: 1 });
      return;
    }
    if (pos.step === 4 && rfq) {
      // Complete outreach server-side so the workflow lands on Quotes & Proposals.
      try {
        await trpClient.completeStep(rfq.rfqId, 4);
      } catch (e) {
        console.error('Failed to record outreach completion:', e);
      }
      goTo({ step: 5, subStep: 1 });
      return;
    }
    if (pos.step < 4) goTo({ step: pos.step + 1, subStep: 1 });
  };

  const handleBack = () => {
    if (pos.step <= 1) return;
    const target = pos.step - 1;
    // Going back into Discovery lands on its true sub-step (results once
    // research started), same rule as rail clicks — never the questionnaire
    // the user already completed.
    goTo({ step: target, subStep: target === 3 ? discoverySubStep(rfq) : 1 });
  };

  const handleStepSelect = (id: string) => {
    const step = STEP_IDS[id];
    // Clickable up to the journey frontier: every data-complete step plus the
    // first incomplete one (where the actual work continues). Steps beyond
    // that are honest "upcoming" — visible, tooltipped, not navigable.
    if (!step || step > journeyFrontier) return;
    // Step 3 has sub-steps: land on discovery's OWN furthest sub-step — the
    // questionnaire while it is genuinely unfinished, the supplier results
    // once it is done. Never derived from later steps: Continue buttons mark
    // those COMPLETED regardless of data, so they prove nothing about
    // discovery.
    const subStep = step === 3 ? discoverySubStep(rfq) : 1;
    goTo({ step, subStep });
  };

  const handleSubStepSelect = (id: string) => {
    const subStep = SUB_STEP_IDS[id];
    if (subStep && pos.step === 3) goTo({ step: 3, subStep });
  };

  const handleRobbieAction = (actionId: string) => {
    if (actionId === 'upload_bom' && pos.step === 1 && !readOnly && uploadDocsRef.current?.openPicker) {
      uploadDocsRef.current.openPicker();
    }
    if (actionId === 'continue_to_outreach' && pos.step === 3) {
      goTo({ step: 4, subStep: 1 });
    }
    if (actionId === 'scroll_to_bottom') {
      const el = document.getElementById('sourcing-strategy-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else mainRef.current?.scrollTo({ top: mainRef.current.scrollHeight, behavior: 'smooth' });
    }
  };

  return {
    goTo, handleBackToDashboard, handleContinue, handleBack, handleStepSelect, handleSubStepSelect, handleRobbieAction,
  };
};
